import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProfileDetails,
  getProfileIdByUsername,
} from "../Services/GetService";
import ProfileCardDashboard from "../Components/ProfileCardDashboard";
import DashboardPageHeader from "../Components/ProfileInfo";
import ProfessionalExperience from "../Components/ProfessionalExperience";
import ProfessionalExperienceOutsideLBMC from "../Components/ProfessionalExperienceOutsideLBMC";
import LicensesCertifications from "../Components/LicensesCertifications";
import Education from "../Components/Education";
import useAccessToken from "../Custom/useAccessToken";

const ProfilePage: React.FC = () => {
  const { accessToken } = useAccessToken();
  const { username } = useParams<{ username?: string }>();
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as {
    from?: "home" | "search";
    query?: string;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!username) {
          setError("No username in URL");
          setIsOwnProfile(false);
          return;
        }
        if (!accessToken) {
          setError("Not authenticated");
          setIsOwnProfile(false);
          return;
        }

        const profileIdForUsername = await getProfileIdByUsername(
          accessToken,
          username
        );

        const currentProfile = await getProfileDetails(accessToken);

        if (profileIdForUsername == currentProfile?.profileId) {
          setProfileId(profileIdForUsername);
          setIsOwnProfile(true);
        } else {
          setProfileId(profileIdForUsername);
          setIsOwnProfile(false);
        }
      } catch (err: any) {
        setError(err.message || "Failed to load profile");
        setProfileId(null);
        setIsOwnProfile(false);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username, accessToken]);

  const handleBack = () => {
    if (navState?.from === "search") {
      const encoded = encodeURIComponent(navState.query!.trim());
      navigate(`/search?criteria=${encoded}`, {
        state: { from: "search", query: navState.query },
      });
    } else {
      navigate("/");
    }
  };

  return (
    <div
      className="p-3 mt-6 surface-ground overflow-auto main-contain"
      style={{ height: "calc(100vh - 48px)" }}
    >
      <div className="mx-auto" style={{ maxWidth: 1040 }}>
        {navState?.from === "search" && (
          <button className="back-link" onClick={handleBack}>
            <i className="pi pi-arrow-left"></i>
            <span>
              {navState?.from === 'search'
                ? "Back to Search Results"
                : "Back to Home"}
            </span>
          </button>
        )}

        {isOwnProfile && (
          <DashboardPageHeader
            title="My Profile"
            disclaimer="Remember: You're in control! Share only what you're comfortable with — your profile, your choice."
            subtitle="Manage your professional information and showcase your expertise."
          />
        )}
        {
          <>
            <ProfileCardDashboard
              profileID={profileId}
              isOwnProfile={isOwnProfile}
            />
            <ProfessionalExperience profileID={profileId} isOwnProfile={isOwnProfile} />
            <ProfessionalExperienceOutsideLBMC
              profileID={profileId}
              isOwnProfile={isOwnProfile}
            />
            <LicensesCertifications
              profileID={profileId}
              isOwnProfile={isOwnProfile}
            />
            <Education profileID={profileId} isOwnProfile={isOwnProfile} />
          </>
        }
      </div>
    </div>
  );
};

export default ProfilePage;
